import { createApi } from '@reduxjs/toolkit/query/react';

import { AdminApi } from '.';
import { getData, postData, putData } from '../../services/API';
import {
  addDelegateToUserUrl,
  addEmployeeToUserUrl,
  addFavoriteUnitsUrl,
  employeeConfigUrl,
  patientsToRoundUrl,
  postEmployeeListConfigUrl,
  profilePicUrl,
  removeDelegateFromUserUrl,
  removeEmployeeFromUserUrl,
  removeFavoriteUnitsUrl,
  updateCurrentFacilityUrl,
  updateRoundSubjectHiddenStatusUrl,
  userAccessUrl,
  userAuthUrl,
  userRoundHistoryUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';

import { cleanPatientToRound } from './utils/cleaners';

interface IGetRoundHistoryPayload {
  facilityId: string;
  startDate: string;
  endDate: string;
  roundingType: string;
}

// TODO: For later use
// interface IUpdateFacPayload {
//   userId: string;
//   facilityId: String;
// }
// interface IUpdateUnitPayload extends IUpdateFacPayload {
//   unitId: string;
// }
// interface IUserPayload {
//   userId: string;
// }
// interface IUserEmployeePayload extends IUserPayload {
//   employeeId: string;
// }
// interface IUserDelegatePayload {
//   delegateId: string;
// }
// interface IUserDelegatesPayload extends IUserPayload {
//   delegateIds: string[];
// }

interface IUpdateHiddenStatusPayload {
  facilityId: string;
  employeeId: string;
  roundingType: string;
  userId: string;
  visible: boolean;
}
interface IGetPatientsToRoundPayload {
  facilityId: string;
  accountNum: string;
  unitId?: string;
}

/**
 * RTK Query API slice for user features.
 *
 * @remarks
 * Provides endpoints for:
 * - User details after authentication (`getAuthorizedUser`, etc.)
 * - User management endpoints as defined
 *
 * @example
 * import { useGetAuthorizedUserQuery } from 'src/redux/apis/Users.api';
 * const { data } = useGetAuthorizedUserQuery(userId);
 */
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: (operation: any) => operation,
  tagTypes: [
    'authorized-user',
    'employee-config',
    'employee-rounding-list-config',
    'employee-subject-config',
    'patient-to-round',
    'patients-to-round-facility',
    // 'employees-to-round',
    'round-history',
    'users'
  ],
  endpoints: builder => ({
    getAuthorizedUser: builder.query({
      query: userId => getData(`${userAuthUrl}?userId=${userId}`, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, userId) => [
        { type: 'authorized-user', id: userId }
      ]
    }),
    postAddEmployeeToUserFavs: builder.mutation({
      query: payload => postData(addEmployeeToUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postUpdateCurrentFacility: builder.mutation({
      query: payload => postData(updateCurrentFacilityUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-subject-config' }
      ]
    }),
    postRemoveEmployeeFromUserFavs: builder.mutation({
      query: payload => postData(removeEmployeeFromUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postUpdateUserProfilePic: builder.mutation({
      query: ({ hcaid, pic }) => {
        const multiFormData = new FormData();
        multiFormData.append('picture', pic);
        return postData(`${profilePicUrl}?userId=${hcaid}`, multiFormData);
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      // TODO: need to reload sas token
      invalidatesTags: () => []
    }),
    postAddFavoriteUnit: builder.mutation({
      query: payload => postData(addFavoriteUnitsUrl, payload),
      transformResponse: processResponse,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Added'
      // });
      transformErrorResponse: processError,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Adding_Failed'
      // });
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId }
      ]
    }),
    postRemoveFavoriteUnit: builder.mutation({
      query: payload => postData(removeFavoriteUnitsUrl, payload),
      transformResponse: processResponse,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Removed'
      // });
      transformErrorResponse: processError,
      // appInsights.trackEvent({
      //   name: 'Primary_Unit_Removal_Failed'
      // });
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId }
      ]
    }),
    postAddDelegateToUser: builder.mutation({
      query: payload => postData(addDelegateToUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postRemoveDelegateFromUser: builder.mutation({
      query: payload => postData(removeDelegateFromUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    postRemoveUserAsDelegate: builder.mutation({
      query: payload => postData(removeDelegateFromUserUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.delegateId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    getEmployeeConfig: builder.query({
      keepUnusedDataFor: 0,
      query: (employeeId: string) =>
        getData(`${employeeConfigUrl}?employeeId=${employeeId}`, {}),
      transformResponse: response => {
        const directEmployees = (response?.directsList ?? []).map(
          (employee: any) => ({
            ...employee,
            hcaid: employee.userKey.split(':').pop(),
            dept: employee.department,
            managerFullName: employee.manager
          })
        );
        const formattedListsDelegatedToMe = (
          response?.roundingListsDelegatedToMe ?? []
        ).map((employee: any) => ({
          ...employee,
          employeeName: `${employee.firstName} ${employee.lastName}`
        }));
        const favEmployees = (response?.favorites ?? []).map(
          (employee: any) => ({
            ...employee,
            hcaid: employee.userKey.split(':').pop(),
            dept: employee.department,
            managerFullName: employee.manager
          })
        );
        return {
          directEmployees,
          delegatedToEmployees: response?.employeesDelegatedTo ?? [],
          delegatedToMeEmployees: formattedListsDelegatedToMe,
          favEmployees
        };
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, employeeId) => [
        { type: 'employee-config', id: employeeId },
        { type: 'employee-config', id: 'LIST' }
      ]
    }),
    getRoundingListConfig: builder.query({
      query: (employeeId: string) =>
        getData(`${employeeConfigUrl}?employeeId=${employeeId}`, {}),
      transformResponse: response => response.myRoundingListConfig,
      transformErrorResponse: processError,
      providesTags: (_result, _error, employeeId) => [
        { type: 'employee-rounding-list-config', id: employeeId }
      ]
    }),
    getEmployeeSubjectConfig: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: { employeeId: string; roundingType: string }) =>
        getData(
          `${employeeConfigUrl}/${payload.roundingType}?employeeId=${payload.employeeId}`,
          {}
        ),
      transformResponse: response =>
        response.map((employee: any) => ({
          ...employee,
          hcaid: employee.userKey?.split(':').pop() ?? '',
          dept: employee.department,
          managerFullName: employee.manager,
          employeeName: `${employee.firstName} ${employee.lastName}`,
          visible: employee.visible
        })),
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-subject-config',
          id: payload.roundingType
        }
      ]
    }),
    updateSubjectHiddenStatus: builder.mutation({
      query: (payload: IUpdateHiddenStatusPayload) => {
        const { facilityId, employeeId, roundingType, visible } = payload;
        return putData(
          `${updateRoundSubjectHiddenStatusUrl}/${roundingType}?facilityId=${facilityId}&id=${employeeId}&visible=${visible}`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        {
          type: 'employee-subject-config',
          id: payload.roundingType
        }
      ]
    }),
    postUpdateRoundingListConfig: builder.mutation({
      query: payload => postData(postEmployeeListConfigUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'authorized-user', id: payload.userId }
      ]
    }),
    getPatientToRound: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientsToRoundPayload) =>
        getData(
          `${patientsToRoundUrl}?facilityId=${payload.facilityId}&accountNum=${payload.accountNum}&unitId=${payload.unitId}`,
          {}
        ),
      transformResponse: response => {
        if (response.length) {
          return response[0];
        }
        return { discharged: true };
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-to-round',
          id: `${payload.facilityId}:${payload.accountNum}:${payload.unitId}`
        }
      ]
    }),
    getPatientsToRoundByFacility: builder.query({
      keepUnusedDataFor: 0,
      query: (facilityId: string) =>
        getData(`${patientsToRoundUrl}?facilityId=${facilityId}`, {}),
      transformResponse: response =>
        response.map((patient: any) => cleanPatientToRound(patient)),
      transformErrorResponse: processError,
      providesTags: (_result, _error, facilityId) => [
        {
          type: 'patients-to-round-facility',
          id: facilityId
        }
      ]
    }),
    // TODO: For later use
    // getEmployeesToRound: builder.query({
    //   query: (payload: IGetEmployeesToRoundPayload) =>
    //     getData(
    //       `${employeesToRoundUrl}?userId=${payload.hcaid}&directReportsOnly=${
    //         payload.directReportsOnly ?? false
    //       }`,
    //       {}
    //     ),
    //   transformResponse: response =>
    //     response.map((employee: any) => cleanEmployeeToRound(employee)),
    //   transformErrorResponse: processError,
    //   providesTags: (_result, _error, payload) => [
    //     {
    //       type: 'employees-to-round',
    //       id: JSON.stringify(payload)
    //     }
    //   ]
    // }),
    getRoundHistory: builder.query({
      query: (payload: IGetRoundHistoryPayload) => {
        const { roundingType, facilityId, startDate, endDate } = payload;
        return getData(
          `${userRoundHistoryUrl}?roundingType=${roundingType}&facilityId=${facilityId}&startDate=${startDate}&endDate=${endDate}`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'round-history',
          id: JSON.stringify(payload)
        }
      ]
    }),
    postUserAccess: builder.mutation({
      query: payload => postData(userAccessUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(AdminApi.util.invalidateTags([{ type: 'users', id: 'LIST' }]));
      }
    })
  })
});

export const {
  useGetAuthorizedUserQuery,
  useLazyGetAuthorizedUserQuery,
  usePostAddEmployeeToUserFavsMutation,
  usePostUpdateCurrentFacilityMutation,
  usePostRemoveEmployeeFromUserFavsMutation,
  usePostUpdateUserProfilePicMutation,
  usePostAddFavoriteUnitMutation,
  usePostRemoveFavoriteUnitMutation,
  usePostAddDelegateToUserMutation,
  usePostRemoveDelegateFromUserMutation,
  usePostRemoveUserAsDelegateMutation,
  useGetEmployeeConfigQuery,
  useLazyGetEmployeeConfigQuery,
  useGetRoundingListConfigQuery,
  useLazyGetRoundingListConfigQuery,
  useGetEmployeeSubjectConfigQuery,
  useLazyGetEmployeeSubjectConfigQuery,
  useUpdateSubjectHiddenStatusMutation,
  usePostUpdateRoundingListConfigMutation,
  useGetPatientToRoundQuery,
  useLazyGetPatientToRoundQuery,
  useGetPatientsToRoundByFacilityQuery,
  useLazyGetPatientsToRoundByFacilityQuery,
  useGetRoundHistoryQuery,
  useLazyGetRoundHistoryQuery,
  usePostUserAccessMutation
} = usersApi;
